/**
 * Workflow designer state — wraps reactflow nodes/edges.
 *
 * react-flow expects nodes with {id, type, position, data} và edges với
 * {id, source, target, label, sourceHandle?}. Chúng ta map sang/từ backend
 * format (WorkflowNode/WorkflowEdge) khi save/load.
 */
import { create } from "zustand";

import type { NodeType, WorkflowEdge, WorkflowGraph, WorkflowNode } from "@/api/workflows";

export interface FlowNode {
  id: string;
  type: string; // backend NodeType used for custom node renderer
  position: { x: number; y: number };
  data: {
    label: string;
    nodeType: NodeType;
    config: Record<string, unknown>;
  };
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string | null;
  /** "true" | "false" cho branch node */
  data?: { condition_label?: string | null };
}

interface State {
  workflowCode: string | null;
  workflowName: string;
  workflowDescription: string;
  versionInfo: { version: number; status: string } | null;
  flowNodes: FlowNode[];
  flowEdges: FlowEdge[];
  selectedNodeId: string | null;
  isDirty: boolean;
  validationErrors: string[];

  // Actions
  loadGraph: (
    graph: WorkflowGraph,
    meta: { code: string; name: string; description: string; version: number; status: string },
  ) => void;
  setMeta: (name: string, description: string) => void;
  selectNode: (id: string | null) => void;
  addNode: (type: NodeType, position?: { x: number; y: number }) => void;
  removeNode: (id: string) => void;
  updateNodeData: (id: string, patch: Partial<FlowNode["data"]>) => void;
  setNodes: (nodes: FlowNode[]) => void;
  setEdges: (edges: FlowEdge[]) => void;
  addEdge: (edge: FlowEdge) => void;
  removeEdge: (id: string) => void;
  updateEdge: (id: string, patch: Partial<FlowEdge>) => void;
  setValidationErrors: (errors: string[]) => void;
}

const NODE_LABEL: Record<NodeType, string> = {
  trigger: "Trigger",
  llm: "LLM",
  tool: "Tool",
  form: "Form",
  branch: "Branch",
  parallel: "Parallel",
  human: "Human",
  notify: "Notify",
  delay: "Delay",
  subflow: "Subflow",
  end: "End",
};

function defaultConfig(type: NodeType): Record<string, unknown> {
  switch (type) {
    case "llm":
      return { prompt: "", model: null, max_tokens: 1024 };
    case "tool":
      return { tool_name: "", args: {} };
    case "branch":
      return { condition: { ">": [{ var: "amount" }, 0] } };
    case "form":
      return { form_code: "", output_key: "form_data" };
    case "human":
      return {
        title: "Phê duyệt",
        allow_actions: ["approve", "reject"],
        timeout_hours: 24,
      };
    case "notify":
      return { channels: ["email"], template_code: "" };
    case "delay":
      return { duration_seconds: 3600 };
    default:
      return {};
  }
}

function genId(type: NodeType, existing: Set<string>): string {
  const base = type;
  let i = 1;
  while (existing.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

export const useWorkflowDesignerStore = create<State>((set, get) => ({
  workflowCode: null,
  workflowName: "",
  workflowDescription: "",
  versionInfo: null,
  flowNodes: [],
  flowEdges: [],
  selectedNodeId: null,
  isDirty: false,
  validationErrors: [],

  loadGraph: (graph, meta) => {
    const flowNodes: FlowNode[] = graph.nodes.map((n, i) => ({
      id: n.id,
      type: "workflowNode",
      position: n.position
        ? { x: n.position.x, y: n.position.y }
        : { x: 250, y: 100 + i * 120 },
      data: {
        label: n.label || n.id,
        nodeType: n.type,
        config: { ...(n.config || {}) },
      },
    }));
    const flowEdges: FlowEdge[] = graph.edges.map((e, i) => ({
      id: e.id || `e_${i}`,
      source: e.from,
      target: e.to,
      label: e.label || (e.condition_label ?? null),
      data: { condition_label: e.condition_label ?? null },
    }));
    set({
      workflowCode: meta.code,
      workflowName: meta.name,
      workflowDescription: meta.description,
      versionInfo: { version: meta.version, status: meta.status },
      flowNodes,
      flowEdges,
      selectedNodeId: null,
      isDirty: false,
      validationErrors: [],
    });
  },

  setMeta: (name, description) =>
    set({
      workflowName: name,
      workflowDescription: description,
      isDirty: true,
    }),

  selectNode: (id) => set({ selectedNodeId: id }),

  addNode: (type, position) => {
    const { flowNodes } = get();
    const ids = new Set(flowNodes.map((n) => n.id));
    const id = genId(type, ids);
    const node: FlowNode = {
      id,
      type: "workflowNode",
      position: position ?? { x: 200 + flowNodes.length * 30, y: 200 },
      data: {
        label: `${NODE_LABEL[type]} ${flowNodes.length + 1}`,
        nodeType: type,
        config: defaultConfig(type),
      },
    };
    set({
      flowNodes: [...flowNodes, node],
      selectedNodeId: id,
      isDirty: true,
    });
  },

  removeNode: (id) =>
    set((s) => ({
      flowNodes: s.flowNodes.filter((n) => n.id !== id),
      flowEdges: s.flowEdges.filter((e) => e.source !== id && e.target !== id),
      selectedNodeId: s.selectedNodeId === id ? null : s.selectedNodeId,
      isDirty: true,
    })),

  updateNodeData: (id, patch) =>
    set((s) => ({
      flowNodes: s.flowNodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...patch } } : n,
      ),
      isDirty: true,
    })),

  setNodes: (nodes) => set({ flowNodes: nodes, isDirty: true }),
  setEdges: (edges) => set({ flowEdges: edges, isDirty: true }),

  addEdge: (edge) =>
    set((s) => ({ flowEdges: [...s.flowEdges, edge], isDirty: true })),

  removeEdge: (id) =>
    set((s) => ({
      flowEdges: s.flowEdges.filter((e) => e.id !== id),
      isDirty: true,
    })),

  updateEdge: (id, patch) =>
    set((s) => ({
      flowEdges: s.flowEdges.map((e) => (e.id === id ? { ...e, ...patch } : e)),
      isDirty: true,
    })),

  setValidationErrors: (errors) => set({ validationErrors: errors }),
}));

// ----------------------------------------------------------------------------
// Conversion helpers — flow ↔ backend graph
// ----------------------------------------------------------------------------
export function flowToGraph(state: State): WorkflowGraph {
  const triggerNode = state.flowNodes.find((n) => n.data.nodeType === "trigger");
  const entry = triggerNode?.id ?? state.flowNodes[0]?.id ?? "start";
  const nodes: WorkflowNode[] = state.flowNodes.map((n) => ({
    id: n.id,
    type: n.data.nodeType,
    label: n.data.label,
    config: n.data.config,
    position: n.position,
  }));
  const edges: WorkflowEdge[] = state.flowEdges.map((e) => ({
    id: e.id,
    from: e.source,
    to: e.target,
    label: typeof e.label === "string" ? e.label : null,
    condition_label: e.data?.condition_label ?? null,
  }));
  return {
    code: state.workflowCode ?? "untitled",
    title: state.workflowName || "Untitled",
    description: state.workflowDescription || null,
    entry,
    nodes,
    edges,
    triggers: {},
  };
}

export const NODE_TYPE_LABELS: Record<NodeType, string> = NODE_LABEL;
